import React, { useState, useEffect } from 'react'
import { CountSOCChart } from './CountSOCChart'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { useTranslations } from '@/hooks/useTranslations'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { mapBuckets } from '../common/labels'
import { HelpPopupButton } from '@/popups/helppopup/help'
import { useModal } from '@/components/modal/Modal'
import { MinSOCPopupBody } from './MinSOCPopup'

type CountSOCBoxProps = {
  services: Services
  session: CurrentSession
  criteria: SearchCriteria
  goToUserPage: (userId: string) => void
  goToMachinePage: (machineId: string) => void
}

// Interface representing a single data entry
export interface CountSOCData {
  bucket: string     // ISO timestamp string
  imei: number     // Device IMEI
  minSoc?: number // minimum soc, if present
  soc45_0?: number // hits in 0-45
  soc60_45?: number // hits in 45-60
  soc80_60?: number // hits in 60-80
  soc100_80?: number // hits in 80-100
}
// Interface representing the full API response (array of entries)
export type SOCDataResponse = CountSOCData[]

export const CountSOCBox = ({ services, session, criteria, goToUserPage, goToMachinePage }: CountSOCBoxProps) => {
  const translates = useTranslations(services, session)
  const [response, setResponse] = useState<SOCDataResponse>()
  const { openModal } = useModal()

  useEffect(() => {
    setResponse(undefined)
    services.session
      .fetchWithToken(`/api/communal/statistics/machines/soc?${SearchCriteriaParams(criteria)}`)
      .then((r) => r.json())
      .then((data) => {
        setResponse(data)
      })
  }, [services.session, criteria])

  if (translates === undefined || !response) {
    return <div className='spinner'></div>
  }

  // Group by hour and sum across IMEIs for all ranges
  const bucketMap: Record<string, { soc45_0: number, soc60_45: number, soc80_60: number, soc100_80: number }> = {}
  response.forEach(row => {
    const bucket = row.bucket
    if (!bucketMap[bucket]) {
      bucketMap[bucket] = { soc45_0: 0, soc60_45: 0, soc80_60: 0, soc100_80: 0 }
    }
    bucketMap[bucket].soc45_0 += row.soc45_0 || 0
    bucketMap[bucket].soc60_45 += row.soc60_45 || 0
    bucketMap[bucket].soc80_60 += row.soc80_60 || 0
    bucketMap[bucket].soc100_80 += row.soc100_80 || 0
  })

  // Sort by hour ascending
  const buckets = Object.keys(bucketMap).sort()

  const labels = buckets.map(bucket => mapBuckets({
    bucket
  }))
  const soc45_0 = buckets.map(bucket => bucketMap[bucket].soc45_0)
  const soc60_45 = buckets.map(bucket => bucketMap[bucket].soc60_45)
  const soc80_60 = buckets.map(bucket => bucketMap[bucket].soc80_60)
  const soc100_80 = buckets.map(bucket => bucketMap[bucket].soc100_80)

  return (
    <div>
      <HelpPopupButton helpKey='page.dashboard.global.box.countsoc.help' translates={translates} />
      <CountSOCChart
        labels={labels}
        dataSets={{
          soc45_0,
          soc60_45,
          soc80_60,
          soc100_80
        }}
        chartTitle={translates.for('page.dashboard.global.box.countsoc.title')}
        dataLabel={translates.for('page.dashboard.global.box.countsoc.data_label')}
        onClick={(i: number) => {
          openModal((props) => MinSOCPopupBody({
            ...props,
            services,
            session,
            translates: translates,
            criteria: criteria,
            bucketFor: new Date(response[i].bucket),
            goToUserPage,
            goToMachinePage
          }))
        }}
      />
    </div>
  )
}